import React from 'react';
import { Phone, ArrowRight, Award, CheckCircle2, Shield } from 'lucide-react';
import { Button } from './ui/button';
import { siteImages } from '../siteImages';

const SERVICES = ['Structural Repairs', 'QA/QC Inspection', 'Corrosion Systems', 'Mechanical Repairs', 'Field Services', 'Protective Coatings'];
const CREDS_FLAT = ['CSLB License No. 1066525 | Class A & C-33', 'DIR No. 2000002659', 'AMPP/NACE Certified', 'DBIDS Gov Contractor', 'TWIC Credentialed', 'DUNS No. 080247052', 'WBENC No. WBE2600603'];

const BcsLogo = ({ size = 420 }) => (
  <div className="relative" style={{ width: size, height: size * 0.48 }}>
    <img src="/bcs-logo-letters.png" alt="BCS" className="absolute inset-0 w-full h-full object-contain drop-shadow-2xl z-0" />
    <img src="/bcs-logo-annotations.png" alt="" className="absolute inset-0 w-full h-full object-contain blueprint-draw z-10" aria-hidden="true" />
  </div>
);

const Label = ({ num, name }) => (
  <div className="bg-slate-800 text-white py-4 px-6 text-center border-b-4 border-orange-500">
    <span className="text-orange-400 font-mono text-sm">B-{num}</span>
    <h2 className="text-xl font-bold mt-1">{name}</h2>
  </div>
);

/* ═══════════════════════════════════════════
   B1 — Clean Corporate: thin line separators, spaced out, refined typography
   ═══════════════════════════════════════════ */
const B1 = () => (
  <section className="pt-28 pb-16 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
    <div className="absolute inset-0" style={{ backgroundImage: `url(${siteImages.hero.main})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
    <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.4) 100%)' }} />
    <div className="max-w-5xl mx-auto relative z-10 text-center">
      <div className="flex justify-center mb-6">
        <BcsLogo size={280} />
      </div>

      {/* Thin separator */}
      <div className="flex items-center justify-center gap-4 mb-6">
        <div className="h-px w-16 bg-white/30" />
        <span className="text-xs text-white/60 uppercase tracking-[0.3em] font-medium">General Contractor — Nationwide</span>
        <div className="h-px w-16 bg-white/30" />
      </div>

      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-5 drop-shadow-lg tracking-tight">
        Infrastructure <span className="text-orange-400">-</span> Engineering <span className="text-orange-400">-</span> Construction
      </h1>
      <p className="text-base sm:text-lg text-white/80 max-w-2xl mx-auto mb-10 leading-relaxed">
        BCS, a division of Corrosion Analytics™, provides nationwide general contracting services for capital improvements, structural rehabilitation, and asset protection across industry.
      </p>

      <div className="flex gap-4 justify-center mb-10">
        <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-10 py-6 text-base shadow-xl rounded-sm uppercase tracking-wider">
          <Phone className="mr-2" size={18} />(949) 371-9948
        </Button>
        <Button size="lg" className="bg-white hover:bg-gray-50 text-slate-900 font-bold px-10 py-6 text-base shadow-xl rounded-sm uppercase tracking-wider">
          Get In Touch <ArrowRight className="ml-2" size={18} />
        </Button>
      </div>

      {/* Services as clean line */}
      <div className="flex flex-wrap gap-x-6 gap-y-2 justify-center mb-10">
        {SERVICES.map((s, i) => (
          <span key={i} className="text-sm text-white/80 font-medium flex items-center gap-2">
            {i > 0 && <span className="text-orange-400/60">|</span>}
            {s}
          </span>
        ))}
      </div>

      {/* Credentials — clean row */}
      <div className="border-t border-white/15 pt-5">
        <div className="flex flex-wrap gap-x-6 gap-y-2 justify-center items-center">
          {CREDS_FLAT.map((c, i) => (
            <span key={i} className="text-[11px] text-white/60 flex items-center gap-1.5 font-medium">
              <CheckCircle2 className="text-green-400/80" size={11} />{c}
            </span>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════
   B2 — Bold & Grounded: larger logo, bigger headline, stronger CTAs, services in pills
   ═══════════════════════════════════════════ */
const B2 = () => (
  <section className="pt-28 pb-16 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
    <div className="absolute inset-0" style={{ backgroundImage: `url(${siteImages.hero.main})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
    <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.18) 45%, rgba(0,0,0,0.5) 100%)' }} />
    <div className="max-w-6xl mx-auto relative z-10 text-center">
      <div className="flex justify-center mb-4">
        <BcsLogo size={360} />
      </div>

      <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white leading-[1.0] mb-4 drop-shadow-xl tracking-tight">
        Infrastructure <span className="text-orange-400">-</span> Engineering <span className="text-orange-400">-</span> Construction
      </h1>
      <p className="text-lg text-white/85 max-w-3xl mx-auto mb-8 leading-relaxed">
        BCS, a division of Corrosion Analytics™, provides nationwide general contracting services for capital improvements, structural rehabilitation, and asset protection across industry.
      </p>

      <div className="flex gap-4 justify-center mb-8">
        <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-12 py-7 text-lg shadow-2xl">
          <Phone className="mr-2" size={22} />(949) 371-9948
        </Button>
        <Button size="lg" className="bg-white hover:bg-gray-50 text-slate-900 font-bold px-12 py-7 text-lg shadow-2xl">
          Get In Touch Today <ArrowRight className="ml-2" size={22} />
        </Button>
      </div>

      <div className="flex flex-wrap gap-2.5 justify-center mb-8">
        {SERVICES.map((s, i) => (
          <span key={i} className="bg-white/15 backdrop-blur-sm border border-white/25 text-white px-5 py-2.5 text-sm rounded-full font-semibold">{s}</span>
        ))}
      </div>

      <div className="inline-flex flex-wrap gap-x-5 gap-y-2 justify-center items-center bg-black/20 backdrop-blur-md rounded-full px-8 py-3 border border-white/10">
        <Award className="text-amber-300" size={15} />
        {CREDS_FLAT.map((c, i) => (
          <span key={i} className="text-[11px] text-white/80 flex items-center gap-1.5">
            <CheckCircle2 className="text-green-300" size={10} />{c}
          </span>
        ))}
      </div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════
   B3 — Refined Minimal: less clutter, elegant spacing, services below as grid
   ═══════════════════════════════════════════ */
const B3 = () => (
  <section className="pt-28 pb-8 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
    <div className="absolute inset-0" style={{ backgroundImage: `url(${siteImages.hero.main})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
    <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.28) 0%, rgba(0,0,0,0.12) 50%, rgba(0,0,0,0.4) 100%)' }} />
    <div className="max-w-5xl mx-auto relative z-10 text-center">
      <div className="flex justify-center mb-8">
        <BcsLogo size={300} />
      </div>

      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4 drop-shadow-lg">
        Infrastructure <span className="text-orange-400">.</span> Engineering <span className="text-orange-400">.</span> Construction
      </h1>
      <p className="text-base text-white/80 max-w-xl mx-auto mb-10 leading-relaxed">
        Nationwide general contracting for capital improvements, structural rehabilitation, and asset protection.
      </p>

      <div className="flex gap-4 justify-center mb-14">
        <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-10 py-6 text-base shadow-xl">
          <Phone className="mr-2" size={18} />(949) 371-9948
        </Button>
        <Button size="lg" className="bg-white hover:bg-gray-50 text-slate-900 font-semibold px-10 py-6 text-base shadow-xl">
          Get In Touch <ArrowRight className="ml-2" size={18} />
        </Button>
      </div>
    </div>

    {/* Services + credentials as a bottom bar */}
    <div className="relative z-10 max-w-6xl mx-auto">
      <div className="bg-black/30 backdrop-blur-md rounded-xl border border-white/10 px-6 py-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap gap-3">
            {SERVICES.map((s, i) => (
              <span key={i} className="text-sm text-white/90 font-medium bg-white/10 rounded-md px-3 py-1.5">{s}</span>
            ))}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 items-center">
            <Shield className="text-amber-300" size={14} />
            {CREDS_FLAT.slice(0, 3).map((c, i) => (
              <span key={i} className="text-[10px] text-white/60 flex items-center gap-1">
                <CheckCircle2 className="text-green-400" size={9} />{c}
              </span>
            ))}
            <span className="text-[10px] text-white/40">+4 more</span>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════
   B4 — Layered Depth: subtle glass card behind content, stacked CTAs
   ═══════════════════════════════════════════ */
const B4 = () => (
  <section className="pt-28 pb-16 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
    <div className="absolute inset-0" style={{ backgroundImage: `url(${siteImages.hero.main})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
    <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.12) 40%, rgba(0,0,0,0.35) 100%)' }} />
    <div className="max-w-4xl mx-auto relative z-10">
      {/* Glass card container */}
      <div className="bg-black/20 backdrop-blur-[6px] rounded-2xl border border-white/10 p-8 sm:p-12 text-center">
        <div className="flex justify-center mb-6">
          <BcsLogo size={300} />
        </div>

        <p className="text-xs text-orange-400 uppercase tracking-[0.35em] font-semibold mb-4">A Division of Corrosion Analytics™</p>

        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-5 drop-shadow-lg tracking-tight">
          Infrastructure <span className="text-orange-400">-</span> Engineering <span className="text-orange-400">-</span> Construction
        </h1>
        <p className="text-base text-white/80 max-w-2xl mx-auto mb-8 leading-relaxed">
          Nationwide general contracting services for capital improvements, structural rehabilitation, and asset protection across industry.
        </p>

        <div className="flex gap-4 justify-center mb-8">
          <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-10 py-6 text-base shadow-xl">
            <Phone className="mr-2" size={18} />(949) 371-9948
          </Button>
          <Button size="lg" className="bg-white hover:bg-gray-50 text-slate-900 font-bold px-10 py-6 text-base shadow-xl">
            Get In Touch Today <ArrowRight className="ml-2" size={18} />
          </Button>
        </div>

        {/* Services row */}
        <div className="flex flex-wrap gap-2 justify-center mb-6">
          {SERVICES.map((s, i) => (
            <span key={i} className="bg-white/10 border border-white/15 text-white/90 px-4 py-2 text-sm rounded-full font-medium">{s}</span>
          ))}
        </div>

        {/* Credentials inside card */}
        <div className="border-t border-white/10 pt-4">
          <div className="flex flex-wrap gap-x-5 gap-y-1.5 justify-center items-center">
            {CREDS_FLAT.map((c, i) => (
              <span key={i} className="text-[11px] text-white/60 flex items-center gap-1.5">
                <CheckCircle2 className="text-green-400/70" size={10} />{c}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════
   B5 — Executive: wider, horizontal credentials bar on top, headline large, services as checkmarks
   ═══════════════════════════════════════════ */
const B5 = () => (
  <section className="pt-20 pb-14 relative overflow-hidden">
    <div className="absolute inset-0" style={{ backgroundImage: `url(${siteImages.hero.main})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
    <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.45) 100%)' }} />

    {/* Top credentials strip */}
    <div className="relative z-10 border-b border-white/10 mb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex flex-wrap gap-x-6 gap-y-1 items-center justify-center">
        <Award className="text-amber-300" size={13} />
        {CREDS_FLAT.map((c, i) => (
          <span key={i} className="text-[10px] text-white/65 flex items-center gap-1.5 font-medium uppercase tracking-wider">
            <CheckCircle2 className="text-green-400/80" size={9} />{c}
          </span>
        ))}
      </div>
    </div>

    <div className="max-w-5xl mx-auto relative z-10 text-center px-4 sm:px-6 lg:px-8">
      <div className="flex justify-center mb-6">
        <BcsLogo size={320} />
      </div>

      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-4 drop-shadow-lg">
        Infrastructure <span className="text-orange-400">-</span> Engineering <span className="text-orange-400">-</span> Construction
      </h1>
      <p className="text-lg text-white/85 max-w-3xl mx-auto mb-10 leading-relaxed">
        BCS, a division of Corrosion Analytics™, provides nationwide general contracting services for capital improvements, structural rehabilitation, and asset protection across industry.
      </p>

      <div className="flex gap-4 justify-center mb-10">
        <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-10 py-6 text-base shadow-xl">
          <Phone className="mr-2" size={18} />(949) 371-9948
        </Button>
        <Button size="lg" className="bg-white hover:bg-gray-50 text-slate-900 font-bold px-10 py-6 text-base shadow-xl">
          Get In Touch Today <ArrowRight className="ml-2" size={18} />
        </Button>
      </div>

      {/* Services as 2-row grid of check items */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
        {SERVICES.map((s, i) => (
          <div key={i} className="bg-black/25 backdrop-blur-sm rounded-lg border border-white/10 p-3 text-center">
            <CheckCircle2 className="text-orange-400 mx-auto mb-1.5" size={18} />
            <span className="text-xs text-white/85 font-semibold leading-tight block">{s}</span>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const HeroPreview = () => (
  <div>
    <div className="bg-orange-500 text-black text-center py-3 px-4 font-bold text-sm sticky top-0 z-50">
      5 VERSIONS OF OPTION B — Centered layout, more professional. Scroll down.
    </div>
    <Label num="1" name="Clean Corporate — Line separators, refined typography, open spacing" />
    <B1 />
    <Label num="2" name="Bold & Grounded — Larger logo, bigger headline, stronger presence" />
    <B2 />
    <Label num="3" name="Refined Minimal — Less clutter, elegant, services in bottom bar" />
    <B3 />
    <Label num="4" name="Layered Glass — Subtle frosted card behind content, contained" />
    <B4 />
    <Label num="5" name="Executive — Credentials strip on top, service icon grid at bottom" />
    <B5 />
    <div className="bg-slate-800 text-white text-center py-8 px-4">
      <p className="text-lg font-bold mb-2">Which version do you prefer?</p>
      <p className="text-slate-400 text-sm">Reply with B-1 through B-5, or tell me what to mix.</p>
    </div>
  </div>
);

export default HeroPreview;
